// @ts-check
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react"
import { useSession } from "../../../../hooks/auth/useSession.hook"
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Table } from "../../../../components/table/Table.component"
import { buildIndividualReportTableColumns } from "./IndividualReportTable.columns"
import useisHoldingModifierDown from "../../../../hooks/useIsHoldingModifierDown"
import { HOLIDAYS_TYPES_ID_TO_LABEL } from "../../../../consts/modules/vacations.consts"
import {
  isIndividualTaskReportItem,
  isTaskReportItem,
} from "../../../../utils/report.utils"
import { useTaskReportsSorting } from "../../../../hooks/reports/useTaskReportsSorting.hook"
import { EmptyTableMessage } from "../../../../components/compounds/customMessages/EmptyTableMessage.compound"
import { useTaskReportsFilters } from "../../../../hooks/reports/useTaskReportsFilters.hook"
import { useTaskReportsCommonFilters } from "../../../../hooks/reports/useTaskReportsCommonFilters.factory"

/**
 * @typedef {import("./IndividualReportTable.controller.types").IndividualReportTableRow} IndividualReportTableRow
 */

/**
 * @type {React.ForwardRefExoticComponent<import("./IndividualReportTable.controller.types").IndividualReportTableControllerProps
 *  & React.RefAttributes<import("./IndividualReportTable.controller.types").IndividualReportTableControllerRef>>}
 */
const IndividualReportTable = forwardRef((props, ref) => {
  const {
    value,
    totals,
    contextMenuRef,
    setContextMenuTarget,
    inSelectionMode,
    setSelectedTasks,
    highlightedTask,
    updatingTask,
    schemaRules,
    teamMemberWorkAnniversaryDate,
    categories,
    modules,
    projects,
    loading,
    infiniteScroll,
    onFilterValuesChange,
  } = props

  const { user } = useSession()
  const userIsAdmin = user?.isAdmin()

  const [expanded, setExpanded] = useState({})
  const [rowSelection, setRowSelection] = useState({})

  const { sorting, handleSortingChange, resetSorting, getSortingValue } =
    useTaskReportsSorting("date")
  const { columnFilters, setColumnFilters, getFilterValues, setFilter } =
    useTaskReportsFilters({
      projectName: "projectCode",
      moduleId: "module",
      taskName: "task",
    })
  const isHoldingModifierDown = useisHoldingModifierDown()

  const {
    categoryFilterTemplate,
    moduleFilterTemplate,
    clientFilterTemplate,
    taskNameFilterTemplate,
  } = useTaskReportsCommonFilters({
    categories,
    modules,
    projects,
    loading,
  })

  const columns = useMemo(
    () =>
      buildIndividualReportTableColumns({
        totals,
        isHoldingModifierDown,
        schemaRules,
        teamMemberWorkAnniversaryDate,
        userIsAdmin,
        updatingTask,
        contextMenuRef,
        setContextMenuTarget,
        clientFilterTemplate,
        moduleFilterTemplate,
        categoryFilterTemplate,
        taskNameFilterTemplate,
        projects,
      }),
    [
      totals,
      isHoldingModifierDown,
      schemaRules,
      teamMemberWorkAnniversaryDate,
      userIsAdmin,
      updatingTask,
      contextMenuRef,
      setContextMenuTarget,
      clientFilterTemplate,
      moduleFilterTemplate,
      categoryFilterTemplate,
      taskNameFilterTemplate,
      projects,
    ]
  )

  const table = useReactTable({
    data: value,
    columns,
    enableSortingRemoval: true,
    sortDescFirst: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    groupedColumnMode: "remove",
    isMultiSortEvent: () => true,
    maxMultiSortColCount: 2,
    initialState: {
      columnPinning: {
        left: ["select", "projectCode"],
      },
      grouping: ["date"],
    },
    state: {
      sorting,
      rowSelection,
      columnVisibility: {
        select: inSelectionMode,
      },
      columnFilters,
      expanded,
    },
    onSortingChange: handleSortingChange,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: (row) => !row.getIsGrouped() && inSelectionMode,
    onColumnFiltersChange: setColumnFilters,
    manualFiltering: true,
  })

  useEffect(() => {
    if (!inSelectionMode) {
      setSelectedTasks([])
      return
    }

    const selectedRowIds = table
      .getSelectedRowModel()
      .flatRows.map((row) => row.original?.taskId)

    const selectedTasks = value
      .filter(isIndividualTaskReportItem)
      .filter((task) => selectedRowIds.includes(task.taskId))
    setSelectedTasks(selectedTasks)
  }, [rowSelection, inSelectionMode, value, table])

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        resetSorting()
        table.resetColumnFilters(true)
        table.resetRowSelection()
      },
      getFilterValues: () => getFilterValues(table),
      getSortingValue: () => getSortingValue(table),
      unselectRow: (taskId) => {
        const row = table
          .getRowModel()
          .flatRows.find((row) => row.original?.taskId === taskId)
        if (row) {
          table.setRowSelection((prev) => ({
            ...prev,
            [row.id]: false,
          }))
        }
      },
      setFilter: (field, value) => setFilter(table, field, value),
    }),
    [table]
  )

  useEffect(() => {
    if (!onFilterValuesChange) {
      return
    }

    onFilterValuesChange(getFilterValues(table))
  }, [columnFilters, onFilterValuesChange, table])

  useEffect(() => {
    table.resetRowSelection()
  }, [value])

  useEffect(() => {
    if (!inSelectionMode) {
      table.resetRowSelection()
    }
  }, [inSelectionMode])

  /**
   * @param {React.MouseEvent} e
   * @param {IndividualReportTableRow} row
   */
  function handleContextMenu(e, row) {
    if (
      !userIsAdmin ||
      !row.original ||
      row.getIsGrouped() ||
      row.original.isPlaceholder
    ) {
      return
    }

    setContextMenuTarget(row.original)
    contextMenuRef?.current?.show(e)
  }

  useEffect(() => {
    /** @type {Record<string, boolean>} */
    const newExpanded = {}

    table.getRowModel().rows.forEach((row) => {
      if (!row.getIsGrouped()) return

      const hasRealTasks = row.subRows.some((r) => isTaskReportItem(r.original))

      if (hasRealTasks) {
        newExpanded[row.id] = true
      }
    })

    setExpanded(newExpanded)
  }, [value])

  /**
   * @param {number} index
   */
  function getRowHeight(index) {
    const rowData = table.getRowModel().rows[index].original

    if (rowData.isPlaceholder) {
      return 37
    }

    return rowData.createdByAdmin || rowData.editedByAdmin ? 38.5 : 37
  }

  /**
   * @param {IndividualReportTableRow} row
   */
  function getRowClassName(row) {
    const rowData = row.original

    if (!row.getIsGrouped()) {
      return rowData.taskId === highlightedTask ? "highlighted-row" : ""
    }

    if ("isVacationDay" in rowData) {
      return !rowData.isPlaceholder ?
          "header-row--non-empty-vacation-day"
        : "header-row--empty-vacation-day"
    }

    if ("isHoliday" in rowData) {
      return `header-row--holiday-${HOLIDAYS_TYPES_ID_TO_LABEL[rowData.holidayTypeId]}-day`
    }

    return ""
  }

  return (
    <Table
      table={table}
      estimatedRowHeight={getRowHeight}
      isLoading={loading}
      emptyMessage={<EmptyTableMessage />}
      className="individual-report__table"
      enableStickyGroupHeaders
      getIsRowSticky={(row) => row.getIsGrouped()}
      onContextMenu={handleContextMenu}
      rowClassName={getRowClassName}
      infiniteScroll={infiniteScroll}
    />
  )
})

export default IndividualReportTable
